/**
 * VendorProfileScreen — /v4/vendor/profile
 *
 * Read-only:  company_name, contact_name, email (from vendors table)
 * Editable:   phone, trade
 * Save:       PATCH /api/vendor/me
 */

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, User, Mail, Phone, Wrench, Check, LogOut } from 'lucide-react'
import { fetchVendorProfile, updateVendorProfile, vendorLogout } from '../../api/vendor'

// ── Field row ─────────────────────────────────────────────────────────────

function ReadOnlyField({
  icon: Icon, label, value,
}: { icon: React.ElementType; label: string; value: string | null }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 0',
      borderBottom: '1px solid var(--line)',
    }}>
      <Icon size={14} color="var(--ink4)" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontSize: 13, color: value ? 'var(--ink)' : 'var(--ink5)', fontStyle: value ? 'normal' : 'italic' }}>
          {value || 'Not set'}
        </div>
      </div>
    </div>
  )
}

function EditableField({
  icon: Icon, label, value, onChange, placeholder,
}: { icon: React.ElementType; label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 0',
      borderBottom: '1px solid var(--line)',
    }}>
      <Icon size={14} color="var(--ink4)" style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
          {label}
        </label>
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '6px 10px',
            fontSize: 13,
            border: '1px solid var(--line)',
            borderRadius: 'var(--rs)',
            background: 'var(--surface)',
            color: 'var(--ink)',
            outline: 'none',
          }}
          onFocus={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
          onBlur={e  => (e.currentTarget.style.borderColor = 'var(--line)')}
        />
      </div>
    </div>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────

export default function VendorProfileScreen() {
  const qc = useQueryClient()
  const [phone, setPhone] = useState('')
  const [trade, setTrade] = useState('')
  const [saved, setSaved]  = useState(false)

  const { data: profile, isLoading, isError } = useQuery({
    queryKey:  ['vendor-profile'],
    queryFn:   fetchVendorProfile,
    staleTime: 60_000,
  })

  // Populate editable fields when data loads
  useEffect(() => {
    if (profile) {
      setPhone(profile.phone ?? '')
      setTrade(profile.trade ?? '')
    }
  }, [profile])

  const mutation = useMutation({
    mutationFn: () => updateVendorProfile({ phone: phone || null, trade: trade || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor-profile'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  const isDirty =
    (phone || '') !== (profile?.phone ?? '') ||
    (trade || '') !== (profile?.trade ?? '')

  if (isLoading) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div className="skel" style={{ width: 140, height: 20, marginBottom: 24 }} />
        <div className="card" style={{ padding: '16px 20px' }}>
          {[0,1,2,3,4].map(i => (
            <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
              <div className="skel" style={{ width: '30%', height: 11, marginBottom: 6 }} />
              <div className="skel" style={{ width: '60%', height: 14 }} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (isError || !profile) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <p style={{ fontSize: 13, color: 'var(--red)' }}>
          Failed to load profile. Please refresh.
        </p>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 4px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>
          Profile
        </h1>
        <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0 }}>
          Your vendor information on file with your contractors
        </p>
      </div>

      <div className="card" style={{ padding: '4px 20px 20px' }}>
        {/* Section: Company (read-only) */}
        <div style={{ padding: '14px 0 4px', fontSize: 11, fontWeight: 700, color: 'var(--ink4)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Company
        </div>

        <ReadOnlyField
          icon={Building2}
          label="Company name"
          value={profile.company_name}
        />
        <ReadOnlyField
          icon={User}
          label="Contact name"
          value={profile.contact_name}
        />
        <ReadOnlyField
          icon={Mail}
          label="Email"
          value={profile.email}
        />

        {/* Section: Contact (editable) */}
        <div style={{ padding: '20px 0 4px', fontSize: 11, fontWeight: 700, color: 'var(--ink4)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Contact
        </div>

        <EditableField
          icon={Phone}
          label="Phone"
          value={phone}
          onChange={setPhone}
          placeholder="(555) 000-0000"
        />
        <EditableField
          icon={Wrench}
          label="Trade"
          value={trade}
          onChange={setTrade}
          placeholder="e.g. Electrical, Plumbing, Framing"
        />

        {/* Save button */}
        <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
          {mutation.isError && (
            <span style={{ fontSize: 12, color: 'var(--red)' }}>
              {(mutation.error as Error).message}
            </span>
          )}
          {saved && (
            <span style={{ fontSize: 12, color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Check size={13} /> Saved
            </span>
          )}
          <button
            className="btn-primary"
            onClick={() => mutation.mutate()}
            disabled={!isDirty || mutation.isPending}
          >
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <p style={{ fontSize: 11, color: 'var(--ink5)', marginTop: 12 }}>
        Company name, contact, and email are managed by your contractor&apos;s address book.
        To update them, contact your contractor directly.
      </p>

      <div style={{ marginTop: 28 }}>
        <button
          type="button"
          className="btn b-gh"
          data-testid="vendor-profile-logout"
          aria-label="Log out"
          onClick={() => void vendorLogout({ loggedOutNote: true })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <LogOut size={14} />
          Log out
        </button>
      </div>
    </div>
  )
}

